import { useApiQuery, useTeam } from '@vrplatform/data';
import { type Maybe, isUUID } from '@vrplatform/utils';
import { mapDepositRow } from '../list/useDepositsTableQuery';
import { useDebouncedValue } from '@mantine/hooks';

export const STORYBOOK_DEPOSIT_DETAIL_QUERY_KEY = 'deposit-detail';

export function useDepositQuery(
  depositId: Maybe<string>,
  view: 'detail' | 'drawer'
) {
  const [{ id: teamId, statementStartAt }] = useTeam();

  const query = useApiQuery(
    async ($api, signal, args) => {
      if (!args.depositId)
        return {
          deposit: null,
        };

      const deposit = await $api
        .GET('/transactions/{id}', {
          params: {
            path: {
              id: args.depositId,
            },
          },
          signal,
        })
        .then((result) => {
          if (result.error) throw result.error;
          return mapDepositRow(result.data, {
            statementStartAt: args.statementStartAt,
          });
        });

      return { deposit };
    },
    {
      keepPreviousData: true,
      queryKeys: [`deposit-detail-${view}`],
      variables: { depositId, statementStartAt, teamId },
      skip: !depositId || !isUUID(depositId),
      queryGroup: 'deposits',
      uniqueQueryKey: 'deposit-detail',
      storybookQueryKey: STORYBOOK_DEPOSIT_DETAIL_QUERY_KEY,
    }
  );

  const [debounced] = useDebouncedValue(query.data, 500);

  return { ...query, data: query.data || debounced };
}

export type Deposit = NonNullable<
  NonNullable<ReturnType<typeof useDepositQuery>['data']>['deposit']
>;
